import type { PopperProps } from '@mui/material/Popper';
import useForkRef from '@mui/utils/useForkRef';
import type { DateRangeValue } from '@src/shared/lib/dateRangePresets';
import { parseDateInput } from '@src/shared/lib/parseDateInput';
import ClickAwayListener from '@src/shared/ui/ClickAwayListener';
import { InputLabel } from '@src/shared/ui/form';
import FormControl from '@src/shared/ui/form/FormControl';
import FormHelperText from '@src/shared/ui/form/FormHelperText';
import { CalendarTodayIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import Paper from '@src/shared/ui/Paper';
import Popper from '@src/shared/ui/Popper';
import Typography from '@src/shared/ui/Typography';
import { format, isValid, parse } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput, { type TextFieldProps } from '../TextInput';
import DateRangePopover, {
  type DateRangePopoverHandle,
} from './DateRangePopover';

export type { DateRangeValue } from '@src/shared/lib/dateRangePresets';

export type DateRangeInputProps = Omit<
  TextFieldProps,
  'onChange' | 'placeholder' | 'value'
> & {
  from?: string | null;
  onApply?: (value: DateRangeValue) => void;
  onClear?: () => void;
  onClose?: () => void;
  popperPlacement?: PopperProps['placement'];
  showCalendarIcon?: boolean;
  to?: string | null;
};

const EMPTY_RANGE: DateRangeValue = { from: null, to: null };
const DATE_PLACEHOLDER = 'dd.mm.yyyy';

function parseApiDate(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
}

function formatApiDate(value: Date | null): string | null {
  return value ? format(value, 'yyyy-MM-dd') : null;
}

function formatInputDate(value?: string | null): string {
  const date = parseApiDate(value);
  return date ? format(date, 'dd.MM.yyyy') : '';
}

function normalizeDateRange(
  fromDate: Date | null,
  toDate: Date | null,
): DateRangeValue {
  if (fromDate && toDate && fromDate > toDate) {
    return {
      from: formatApiDate(toDate),
      to: formatApiDate(fromDate),
    };
  }
  return {
    from: formatApiDate(fromDate),
    to: formatApiDate(toDate),
  };
}

function DateRangeInput({
  disabled,
  error,
  from = null,
  fullWidth,
  helperText,
  inputRef,
  label,
  margin = 'normal',
  name,
  onApply,
  onClear,
  onClose,
  onClick,
  onKeyDown,
  popperPlacement = 'bottom-start',
  required,
  showCalendarIcon = true,
  size = 'small',
  slotProps,
  sx,
  to = null,
  ...textFieldProps
}: DateRangeInputProps) {
  const { t } = useTranslation();
  const [activeDateField, setActiveDateField] = useState<'from' | 'to' | null>(
    null,
  );
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<DateRangeValue>({ from, to });
  const [fromText, setFromText] = useState(() => formatInputDate(from));
  const [toText, setToText] = useState(() => formatInputDate(to));
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const fromInputRef = useRef<HTMLInputElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const popoverPointerDownRef = useRef(false);
  const popoverRef = useRef<DateRangePopoverHandle>(null);
  const skipNextBlurCommitRef = useRef(false);
  const toInputRef = useRef<HTMLInputElement | null>(null);
  const mergedFromInputRef = useForkRef(fromInputRef, inputRef);

  useEffect(() => {
    setValue({ from, to });
    setFromText(formatInputDate(from));
    setToText(formatInputDate(to));
  }, [from, to]);

  const syncDraft = (nextValue: DateRangeValue) => {
    setValue(nextValue);
    setFromText(formatInputDate(nextValue.from));
    setToText(formatInputDate(nextValue.to));
  };

  const focusToInput = () => {
    setActiveDateField('to');
    window.requestAnimationFrame(() => toInputRef.current?.focus());
  };

  const focusFromInput = () => {
    setActiveDateField('from');
    window.requestAnimationFrame(() => fromInputRef.current?.focus());
  };

  const moveFocusAtBoundary = (
    event: React.KeyboardEvent<HTMLDivElement>,
    direction: 'left' | 'right',
    targetRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return false;

    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    if (selectionStart === null || selectionEnd === null) return false;
    if (selectionStart !== selectionEnd) return false;

    const isAtBoundary =
      direction === 'right'
        ? selectionEnd === input.value.length
        : selectionStart === 0;
    const target = targetRef.current;
    if (!isAtBoundary || !target) return false;

    event.preventDefault();
    skipNextBlurCommitRef.current = true;
    target.focus();
    const caretPosition = direction === 'right' ? 0 : target.value.length;
    target.setSelectionRange(caretPosition, caretPosition);
    return true;
  };

  const focusNextElement = () => {
    const currentInput = toInputRef.current;
    if (!currentInput) return;

    window.requestAnimationFrame(() => {
      const focusableElements = Array.from(
        currentInput.ownerDocument.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.hidden &&
          element.getClientRects().length > 0,
      );
      const currentIndex = focusableElements.indexOf(currentInput);
      if (currentIndex >= 0) {
        focusableElements[currentIndex + 1]?.focus();
      }
    });
  };

  const handlePopoverChange = (nextValue: DateRangeValue) => {
    syncDraft(nextValue);
    if (activeDateField === 'from' && nextValue.from && !nextValue.to) {
      focusToInput();
    }
  };

  const resetDraft = () => syncDraft({ from, to });

  const close = (reset = true) => {
    setOpen(false);
    if (reset) resetDraft();
    onClose?.();
  };

  const openPopover = () => {
    if (!disabled) setOpen(true);
  };

  const commitFromInput = () => {
    const parsed = parseDateInput(fromText, new Date()).date;
    if (fromText.trim() && !parsed) {
      setFromText(formatInputDate(value.from));
      return false;
    }
    if (popoverRef.current) {
      popoverRef.current.setFromDate(parsed);
      return Boolean(parsed);
    }
    syncDraft(normalizeDateRange(parsed, parseApiDate(value.to)));
    return Boolean(parsed);
  };

  const commitToInput = () => {
    const parsed = parseDateInput(toText, new Date()).date;
    if (toText.trim() && !parsed) {
      setToText(formatInputDate(value.to));
      return false;
    }
    if (popoverRef.current) {
      popoverRef.current.setToDate(parsed);
      return Boolean(parsed);
    }
    syncDraft(normalizeDateRange(parseApiDate(value.from), parsed));
    return Boolean(parsed);
  };

  const handleInputBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    commit: () => void,
  ) => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }
    const nextFocused = event.relatedTarget as Node | null;
    if (
      popoverPointerDownRef.current ||
      (nextFocused &&
        (anchorRef.current?.contains(nextFocused) ||
          popoverContentRef.current?.contains(nextFocused)))
    ) {
      return;
    }
    commit();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    commit: () => boolean,
    onCommit?: () => void,
    onTab?: () => void,
    onHorizontalArrow?: (event: React.KeyboardEvent<HTMLDivElement>) => boolean,
  ) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (
      (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
      onHorizontalArrow?.(event)
    ) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      openPopover();
      const committed = commit();
      if (committed) onCommit?.();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      openPopover();
    } else if (event.key === 'Escape') {
      close();
    } else if (event.key === 'Tab' && !event.shiftKey && onTab) {
      event.preventDefault();
      const committed = commit();
      if (committed) {
        skipNextBlurCommitRef.current = true;
        onTab();
      }
    }
  };

  const inputSlotProps = {
    ...(slotProps?.input as Record<string, unknown>),
    disableUnderline: true,
  };
  const htmlInputSlotProps = slotProps?.htmlInput as
    | Record<string, unknown>
    | undefined;

  return (
    <>
      <FormControl
        ref={anchorRef}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        margin={margin}
        required={required}
        sx={[styles.formControl, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {label ? <InputLabel shrink>{label}</InputLabel> : null}
        <Box
          sx={styles.frame(Boolean(error), Boolean(disabled), Boolean(label))}
          onClick={(event) => {
            onClick?.(event as React.MouseEvent<HTMLDivElement>);
            openPopover();
          }}
        >
          {showCalendarIcon ? (
            <CalendarTodayIcon sx={styles.calendarIcon} />
          ) : null}
          <TextInput
            {...textFieldProps}
            disabled={disabled}
            inputRef={mergedFromInputRef}
            margin="none"
            name={name ? `${name}.from` : undefined}
            placeholder={DATE_PLACEHOLDER}
            size={size}
            value={fromText}
            variant="standard"
            onBlur={(event) => handleInputBlur(event, commitFromInput)}
            onChange={(event) => setFromText(event.target.value)}
            onFocus={() => {
              setActiveDateField('from');
              openPopover();
            }}
            onKeyDown={(event) =>
              handleKeyDown(
                event,
                commitFromInput,
                focusToInput,
                focusToInput,
                (keyboardEvent) =>
                  keyboardEvent.key === 'ArrowRight' &&
                  moveFocusAtBoundary(keyboardEvent, 'right', toInputRef),
              )
            }
            slotProps={{
              input: inputSlotProps,
              htmlInput: {
                ...htmlInputSlotProps,
                'aria-label': t('common.from_label', 'From'),
              },
            }}
            sx={styles.dateInput}
          />
          <Typography aria-hidden sx={styles.separator}>
            -
          </Typography>
          <TextInput
            {...textFieldProps}
            disabled={disabled}
            inputRef={toInputRef}
            margin="none"
            name={name ? `${name}.to` : undefined}
            placeholder={DATE_PLACEHOLDER}
            size={size}
            value={toText}
            variant="standard"
            onBlur={(event) => handleInputBlur(event, commitToInput)}
            onChange={(event) => setToText(event.target.value)}
            onFocus={() => {
              setActiveDateField('to');
              openPopover();
            }}
            onKeyDown={(event) =>
              handleKeyDown(
                event,
                commitToInput,
                () => {
                  if (!value.from) focusFromInput();
                },
                undefined,
                (keyboardEvent) =>
                  keyboardEvent.key === 'ArrowLeft' &&
                  moveFocusAtBoundary(keyboardEvent, 'left', fromInputRef),
              )
            }
            slotProps={{
              input: inputSlotProps,
              htmlInput: {
                ...htmlInputSlotProps,
                'aria-label': t('common.to_label', 'To'),
              },
            }}
            sx={styles.dateInput}
          />
        </Box>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>

      <Popper
        open={open && !disabled}
        anchorEl={anchorRef.current}
        placement={popperPlacement}
        sx={styles.popper}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) return;
            close();
          }}
        >
          <Paper
            ref={popoverContentRef}
            elevation={4}
            sx={styles.paper}
            onPointerDownCapture={() => {
              popoverPointerDownRef.current = true;
              window.setTimeout(() => {
                popoverPointerDownRef.current = false;
              });
            }}
          >
            <DateRangePopover
              ref={popoverRef}
              activeDateField={activeDateField}
              autoApply
              from={value.from}
              to={value.to}
              showActions={false}
              showDateFields={false}
              onChange={handlePopoverChange}
              onApply={(nextValue) => {
                syncDraft(nextValue);
                onApply?.(nextValue);
                close(false);
                focusNextElement();
              }}
              onClear={() => {
                syncDraft(EMPTY_RANGE);
                onClear?.();
                if (!onClear) onApply?.(EMPTY_RANGE);
                close(false);
              }}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

const styles = {
  calendarIcon: {
    color: 'action.active',
    flexShrink: 0,
    fontSize: 24,
  },
  dateInput: {
    width: '74px',
    display: 'inline-block',
    minWidth: 0,
    verticalAlign: 'middle',
    '& .MuiInput-root': {
      backgroundColor: 'transparent',
      border: 0,
      height: '100%',
      display: 'block',
      '& .MuiInput-input': {
        height: '100%',
        minWidth: 0,
        p: 0,
      },
    },
    '& .MuiFormControl-root': {
      m: 0,
    },
  },
  formControl: {
    minWidth: 0,
  },
  frame: (error: boolean, disabled: boolean, hasLabel: boolean) => ({
    alignItems: 'center',
    backgroundColor: disabled ? 'grey.100' : 'common.white',
    border: '1px solid',
    borderColor: error ? 'error.main' : 'grey.600',
    borderRadius: 1,
    display: 'flex',
    gap: 1,
    height: 32,
    mt: hasLabel ? '18px' : 0,
    overflow: 'hidden',
    px: '10px',
    py: '5px',
    '&:focus-within': {
      borderColor: 'common.black',
    },
    position: 'relative',
  }),
  paper: { mt: 0.5 },
  popper: { zIndex: 1400 },
  separator: {
    fontSize: '14px',
    lineHeight: 1,
    width: '8px',
    display: 'inline-block',
  },
} as const;

export default DateRangeInput;
